import { useEffect, useMemo, useState } from "react";
import { api, errMsg, money } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { IndianRupee, ShoppingCart, TrendingUp, Percent } from "lucide-react";

function dayKey(iso) { return (iso || "").slice(0, 10); }

export default function Reports() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders?limit=100");
        setOrders(data.items || []);
      } catch (e) { errMsg(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const { revenue, orderCount, aov, cancelRate, daily, byPayment } = useMemo(() => {
    const active = orders.filter((o) => !["cancelled", "refunded"].includes(o.status));
    const revenue = active.reduce((s, o) => s + Number(o.pricing?.total || 0), 0);
    const orderCount = active.length;
    const aov = orderCount ? revenue / orderCount : 0;
    const cancelled = orders.filter((o) => o.status === "cancelled").length;
    const cancelRate = orders.length ? (cancelled / orders.length) * 100 : 0;

    // Group last 14 days
    const map = new Map();
    active.forEach((o) => {
      const k = dayKey(o.created_at);
      const cur = map.get(k) || { date: k, revenue: 0, orders: 0 };
      cur.revenue += Number(o.pricing?.total || 0);
      cur.orders += 1;
      map.set(k, cur);
    });
    const daily = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(-14)
      .map((d) => ({ ...d, label: d.date.slice(5) }));

    const paymentMap = {};
    active.forEach((o) => {
      const m = (o.payment?.method || "unknown").toUpperCase();
      paymentMap[m] = (paymentMap[m] || 0) + Number(o.pricing?.total || 0);
    });
    const byPayment = Object.entries(paymentMap).map(([method, amount]) => ({ method, amount }));

    return { revenue, orderCount, aov, cancelRate, daily, byPayment };
  }, [orders]);

  return (
    <div className="p-8" data-testid="reports-page">
      <PageHeader title="Reports" subtitle={`Last ${orders.length} orders — live snapshot`} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={money(revenue)} icon={IndianRupee} tone="primary" testId="report-revenue" />
        <StatCard label="Orders" value={orderCount} icon={ShoppingCart} tone="accent" testId="report-orders" />
        <StatCard label="Avg order value" value={money(aov)} icon={TrendingUp} tone="muted" testId="report-aov" />
        <StatCard label="Cancel rate" value={`${cancelRate.toFixed(1)}%`} icon={Percent} tone={cancelRate > 5 ? "warn" : "muted"} testId="report-cancel-rate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="lg:col-span-2 card-soft border-transparent">
          <CardHeader><CardTitle className="font-display text-xl">Orders per day</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64" data-testid="orders-chart">
              {daily.length === 0 && !loading && <div className="text-center text-muted-foreground py-16">Not enough data yet.</div>}
              {daily.length > 0 && (
                <ResponsiveContainer>
                  <LineChart data={daily} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 30% 88%)" />
                    <XAxis dataKey="label" stroke="hsl(24 8% 40%)" fontSize={11} />
                    <YAxis stroke="hsl(24 8% 40%)" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(34 30% 88%)", background: "white" }} />
                    <Line type="monotone" dataKey="orders" stroke="hsl(20 87% 40%)" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(20 87% 40%)" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-soft border-transparent">
          <CardHeader><CardTitle className="font-display text-xl">Revenue by payment</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64" data-testid="payment-chart">
              {byPayment.length === 0 && !loading && <div className="text-center text-muted-foreground py-16">No revenue yet.</div>}
              {byPayment.length > 0 && (
                <ResponsiveContainer>
                  <BarChart data={byPayment} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 30% 88%)" />
                    <XAxis dataKey="method" stroke="hsl(24 8% 40%)" fontSize={11} />
                    <YAxis stroke="hsl(24 8% 40%)" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(34 30% 88%)", background: "white" }} formatter={(v) => money(v)} />
                    <Bar dataKey="amount" fill="hsl(var(--brand-red))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
